import {gsap} from 'gsap'
import {parseScramblePhrases, runScrambleCycle} from './_scramble.js'

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const yOffset = prefersReducedMotion ? '0' : '1rem';

// `:not(del *)` keeps the heading fade-in off the per-character spans the
// scramble effect renders inside <del>; the <del> itself still fades in.
gsap.set(' .hero .top-section .top-text-wrapper *:not(del *)', {autoAlpha: 0, y: yOffset});
gsap.set(' .hero .bottom-section .left > *', {autoAlpha: 0, x: prefersReducedMotion ? 0 : '-1.5rem'});
gsap.set(' .hero .bottom-section .left .button', {autoAlpha: 0, x: 0, y: prefersReducedMotion ? 0 : '0.5rem', scale: prefersReducedMotion ? 1 : 0.96});
gsap.set(' .hero .bottom-section .right figure .image', {
    clipPath: prefersReducedMotion ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)',
});
gsap.set(' .hero .bottom-section .right figure .image img, .hero .bottom-section .right figure .image video', {scale: prefersReducedMotion ? 1 : 1.06});
gsap.set(' .hero .bottom-section .right figure.has-drawer .hero-drawer, .hero .bottom-section .right figure.has-drawer figcaption', {
    yPercent: prefersReducedMotion ? 0 : -100,
    autoAlpha: prefersReducedMotion ? 1 : 0,
});
gsap.set(' .hero .bottom-section .right figure:not(.has-drawer) figcaption', {
    autoAlpha: 0,
    y: prefersReducedMotion ? 0 : '0.4rem',
});
gsap.set(' .hero .hero-stage__drawer', {
    autoAlpha: prefersReducedMotion ? 1 : 0,
    xPercent: prefersReducedMotion ? 0 : 100,
});
gsap.set(' .image-gallery li', {autoAlpha: 0.3, scale: 0.95, y: yOffset});
gsap.set(' .info-boxes .box-item', {autoAlpha: 0, y: prefersReducedMotion ? 0 : '1rem'});
// Pointers have two reveal modes, chosen per block with data-reveal on the list:
//
//  "stack"  — every card collapses onto the first one and is dealt back out
//             from the top, last card first. It only reads well with a few
//             distinctly coloured cards near the top of the page (the
//             frontpage), so it is opt-in via the block's toggle.
//  "settle" — the default. The first card stays put; every card after it
//             starts tucked further over the previous one and slides down into
//             its normal overlap once the list scrolls into view. Cards stay
//             visible the whole time, so a long list mid-page never shows a
//             hole while it waits to animate.
const POINTER_SETTLE_OFFSET = '-7rem';
const pointerLists = Array.from(document.querySelectorAll('.pointer-items'));
const stackPointerLists = pointerLists.filter(list => list.dataset.reveal === 'stack');
const settlePointerLists = pointerLists.filter(list => list.dataset.reveal !== 'stack');

const stackPointers = (list) => {
    const cards = list.querySelectorAll('.pointer');
    if (!cards.length) return;
    const firstTop = cards[0].offsetTop;
    cards.forEach(card => {
        gsap.set(card, {
            autoAlpha: 1,
            y: prefersReducedMotion ? 0 : -(card.offsetTop - firstTop)
        });
    });
};
stackPointerLists.forEach(stackPointers);
// Re-measure once media inside the list has loaded layout dimensions
if (!prefersReducedMotion) {
    window.addEventListener('load', () => {
        stackPointerLists.forEach(list => {
            if (list.dataset.pointersRevealed === '1') return;
            stackPointers(list);
        });
    }, { once: true });
}

settlePointerLists.forEach(list => {
    list.querySelectorAll('.pointer').forEach((card, i) => {
        gsap.set(card, {
            autoAlpha: 1,
            y: (prefersReducedMotion || i === 0) ? 0 : POINTER_SETTLE_OFFSET,
        });
    });
});

const animateVisible = (block, ratio, isIntersecting) => {
    // When the element is in view:
    if (ratio > 0 && isIntersecting) {
        // Animate all the children of this element
        switch (block.dataset.animation) {
            case 'hero':
                const tl = gsap.timeline();

                tl.to(block.querySelectorAll(' .top-section .top-text-wrapper *:not(del *)'), {
                    duration: 0.55,
                    autoAlpha: 1,
                    y: '0',
                    stagger: 0.06,
                    ease: 'power3.out',
                    onComplete: () => {
                        const phrases = parseScramblePhrases(block);
                        const del = block.querySelector('del.scramble-cycle');
                        if (phrases && del) runScrambleCycle(del, phrases);
                    },
                });

                const leftItems = block.querySelectorAll(' .bottom-section .left > *:not(.button)');
                if (leftItems.length) {
                    tl.to(leftItems, {
                        duration: 0.6,
                        autoAlpha: 1,
                        x: 0,
                        stagger: 0.07,
                        ease: 'power3.out',
                    }, 0.3);
                }

                const imageWrap = block.querySelector(' .bottom-section .right figure .image');
                const imageMedia = block.querySelectorAll(' .bottom-section .right figure .image img, .bottom-section .right figure .image video');
                if (imageWrap) {
                    tl.to(imageWrap, {
                        duration: 1.0,
                        clipPath: 'inset(0 0 0 0)',
                        ease: 'power3.out',
                    }, 0.4);
                }
                if (imageMedia.length) {
                    tl.to(imageMedia, {
                        duration: 0.9,
                        scale: 1,
                        ease: 'power4.out',
                    }, 0.4);
                }

                const button = block.querySelector(' .bottom-section .left .button');
                if (button) {
                    tl.to(button, {
                        duration: 0.45,
                        autoAlpha: 1,
                        y: 0,
                        scale: 1,
                        ease: 'power3.out',
                    }, 0.55);
                }

                const stageDrawers = block.querySelectorAll('.hero-stage__drawer');
                if (stageDrawers.length) {
                    tl.to(stageDrawers, {
                        duration: 0.5,
                        autoAlpha: 1,
                        xPercent: 0,
                        stagger: { each: 0.1, from: 'end' },
                        ease: 'ease-in-out',
                    }, 0.45);
                }

                const figureEl = block.querySelector(' .bottom-section .right figure');
                const hasDrawer = figureEl && figureEl.classList.contains('has-drawer');
                const figcaption = figureEl && figureEl.querySelector('figcaption');

                if (hasDrawer) {
                    // Drawer + caption fan out from behind the image with the same
                    // stagger/ease as the pointer cards so the motion feels unified.
                    const drawerBack = figureEl.querySelector('.hero-drawer--back');
                    const drawerStack = [drawerBack, figcaption].filter(Boolean);
                    if (drawerStack.length) {
                        tl.to(drawerStack, {
                            duration: 0.5,
                            autoAlpha: 1,
                            yPercent: 0,
                            stagger: { each: 0.1, from: 'start' },
                            ease: 'ease-in-out',
                        }, 1.4);
                    }
                } else if (figcaption) {
                    tl.to(figcaption, {
                        duration: 0.35,
                        autoAlpha: 1,
                        y: 0,
                        ease: 'power3.out',
                    }, 1.0);
                }
                break;
            case "image-gallery":
                gsap.to(block.querySelectorAll('li'), {
                    duration: 0.6,
                    autoAlpha: 1,
                    y: '0',
                    stagger: 0.08,
                    scale: 1,
                    ease: 'power3.out',
                })
                break;
            case "info-boxes":
                gsap.to(block.querySelectorAll('.box-item'), {
                    duration: 0.55,
                    autoAlpha: 1,
                    y: 0,
                    stagger: 0.07,
                    ease: 'power3.out',
                });
                break;
            case "kpi": {
                if (prefersReducedMotion) break;
                const ints = block.querySelectorAll('.kpi-int');
                ints.forEach(el => {
                    const raw = el.textContent.trim();

                    // Fraction like "9/10" — animate the numerator only
                    const fractionMatch = raw.match(/^(\d+)(\/\d+)$/);
                    if (fractionMatch) {
                        const target = parseInt(fractionMatch[1], 10);
                        const tail = fractionMatch[2];
                        const obj = {val: 0};
                        el.textContent = '0' + tail;
                        gsap.to(obj, {
                            val: target,
                            duration: 1.6,
                            ease: 'power2.out',
                            onUpdate: () => {
                                el.textContent = Math.round(obj.val) + tail;
                            },
                            onComplete: () => {
                                el.textContent = target + tail;
                            },
                        });
                        return;
                    }

                    // Integer or decimal (Norwegian comma supported)
                    const numericMatch = raw.match(/^(\d+)(?:[.,](\d+))?$/);
                    if (!numericMatch) return;
                    const target = parseFloat(raw.replace(',', '.'));
                    if (Number.isNaN(target)) return;
                    const decimals = numericMatch[2] ? numericMatch[2].length : 0;
                    const separator = raw.includes(',') ? ',' : '.';
                    const obj = {val: 0};
                    el.textContent = (0).toFixed(decimals).replace('.', separator);
                    gsap.to(obj, {
                        val: target,
                        duration: 1.6,
                        ease: 'power2.out',
                        onUpdate: () => {
                            el.textContent = obj.val.toFixed(decimals).replace('.', separator);
                        },
                        onComplete: () => {
                            el.textContent = raw;
                        },
                    });
                });
                break;
            }
        }

    }
}

const blocks = document.querySelectorAll(' #content-section section');
const blocksObserver = new IntersectionObserver(
    entries => {
        return entries.forEach(event => {
            const {target, intersectionRatio, isIntersecting} = event;
            animateVisible(target, intersectionRatio, isIntersecting);
            if (isIntersecting) {
                blocksObserver.unobserve(target);
            }
        });
    },
    {threshold: 0.2}
);
for (const block of blocks) {
    blocksObserver.observe(block);
}

// Pointers, stack mode: one trigger per list, fired as soon as the list
// reaches the bottom of the viewport so the deal has time to finish.
const pointersStackObserver = new IntersectionObserver(
    entries => {
        entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting) return;
            target.dataset.pointersRevealed = '1';
            gsap.to(target.querySelectorAll('.pointer'), {
                duration: 0.5,
                y: 0,
                delay: 0.4,
                stagger: { each: 0.1, from: 'end' },
                // GSAP's default; named explicitly so a library update can't change the look.
                ease: 'power1.out'
            });
            pointersStackObserver.unobserve(target);
        });
    },
    { threshold: 0, rootMargin: '0px 0px -5% 0px' }
);
stackPointerLists.forEach(list => pointersStackObserver.observe(list));

// Pointers, settle mode: one trigger per list, fired once the list is ~15%
// into the viewport. All cards settle together with a short stagger from the
// top, so nothing below is left tucked while the visitor reads.
const pointersSettleObserver = new IntersectionObserver(
    entries => {
        entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting) return;
            target.dataset.pointersRevealed = '1';
            gsap.to(target.querySelectorAll('.pointer:not(:first-child)'), {
                duration: 0.6,
                y: 0,
                stagger: { each: 0.06, from: 'start' },
                ease: 'power3.out',
            });
            pointersSettleObserver.unobserve(target);
        });
    },
    { threshold: 0, rootMargin: '0px 0px -15% 0px' }
);
settlePointerLists.forEach(list => pointersSettleObserver.observe(list));
